import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

/*
  Dark mode, resolved in this order: what the person chose here, else what
  their operating system says. Stored under one key and applied by toggling
  `dark` on <html>, which is what tailwind.config.js `darkMode: 'class'` reads.

  The class is written in index.html before React mounts (see the inline script
  there) so the first paint is already the right theme. Without that, a person
  on dark gets a white flash on every page load.
*/

type Choice = 'light' | 'dark' | 'system';

const KEY = 'safepass.theme';

interface ThemeValue {
  choice: Choice;
  /** What is actually on screen once `system` is resolved. */
  resolved: 'light' | 'dark';
  setChoice: (c: Choice) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

function systemPrefersDark() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function readStored(): Choice {
  const raw = localStorage.getItem(KEY);
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<Choice>(() => readStored());
  const [systemDark, setSystemDark] = useState(() => systemPrefersDark());

  /* Follow the OS while the choice is `system` — someone whose Mac switches at
     sunset expects this to switch with it, without a reload. */
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolved: 'light' | 'dark' = choice === 'system' ? (systemDark ? 'dark' : 'light') : choice;

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
    document.documentElement.style.colorScheme = resolved;
  }, [resolved]);

  const setChoice = useCallback((c: Choice) => {
    setChoiceState(c);
    localStorage.setItem(KEY, c);
  }, []);

  /* The bar's button is a two-state switch, so it commits to an explicit
     choice rather than cycling back through `system` where the next click
     would appear to do nothing. */
  const toggle = useCallback(() => {
    setChoice(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setChoice]);

  return (
    <ThemeContext.Provider value={{ choice, resolved, setChoice, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
